import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
  CollectionHero,
  CollectionTopBar,
  GuardianPanel,
  RelicStorePanel,
} from './store/StoreView';
import PurchaseModal from './store/PurchaseModal';
import VaultBrowser from './vault/VaultBrowser';
import RelicDetailModal from './vault/RelicDetailModal';
import BadgesView from './badges/BadgesView';
import BadgeDetailModal from './badges/BadgeDetailModal';
import { useStorePurchaseFlow } from './hooks/useStorePurchaseFlow';
import { useVaultBrowser } from './hooks/useVaultBrowser';
import { useCollectionCatalog } from './shared/useCollectionCatalog';
import { useRelicVaultInsets } from './shared/safeArea';
import { useRelicVaultLayout } from './shared/useRelicVaultLayout';
import { COLORS, bodyFont, headingFont, pageBackground } from './shared/theme';
import { DEFAULT_COLLECTION_TAB, TOUR_COLLECTION_TABS, tourOnlyCatalog } from './collectionTabs';
import { hydrateRelicVault, loadCatalog, refreshStoreSlots } from 'store/relicVault';
import { ALL_GUILDS_ID, relicsForGuild } from 'utils/relicVault/catalog';

import { TYPE } from 'style/typography';

/**
 * The Collection Store, opened full-screen from the metaverse HUD. Three tabs:
 *
 *   Guardians  the nine companions, bought with QuikCoins and equipped here
 *   Relics     the five store slots, with the full relic collection below them
 *   Badges     every badge in the catalog, earned by playing rather than bought
 *
 * Everything shown comes from the backend: the catalogs, what the learner owns,
 * and which five relics are on offer. `balance` is the QuikCoins balance to
 * display; `onBalance` carries back the balance the server reports after a
 * purchase, sale or refresh.
 */
const RelicVaultOverlay = ({ visible, onClose, balance, token, onBalance, tourOnly = false }) => {
  const dispatch = useDispatch();
  const vault = useSelector(state => state.relicVault);
  const catalog = useCollectionCatalog();
  const [tab, setTab] = useState(DEFAULT_COLLECTION_TAB);
  const [selectedBadgeId, setSelectedBadgeId] = useState(null);
  const { isTablet, isPhone } = useRelicVaultLayout();
  // Keep both top bars clear of the status bar / notch, so their close button
  // stays tappable. Padding the root means the page background still paints
  // behind the inset instead of leaving a bare strip.
  const insets = useRelicVaultInsets();
  // The store panel's "My Vault" chest scrolls down to the collection below it.
  const scrollRef = useRef(null);
  // A tab switch starts from the top (owner 12 Oct #5; Sol owner-r24 Q7 A).
  const changeTab = next => {
    if (next !== tab) scrollRef.current?.scrollTo({ y: 0, animated: false });
    setTab(next);
  };
  const collectionY = useRef(0);

  const purchase = useStorePurchaseFlow({ token, onBalance });
  const browser = useVaultBrowser({ token, onBalance });

  // Load on open rather than on mount: the store is one screen of many, and the
  // relic catalog is 675 rows. Both calls are no-ops once they have succeeded.
  useEffect(() => {
    if (!visible) return;
    setTab(tourOnly ? 'badges' : DEFAULT_COLLECTION_TAB);
    setSelectedBadgeId(null);
    dispatch(loadCatalog({ token }));
    if (token) dispatch(hydrateRelicVault({ token }));
  }, [dispatch, token, tourOnly, visible]);

  const selectedRelic = useMemo(() => {
    if (!browser.selectedRelicId) return null;
    return (
      relicsForGuild(catalog, ALL_GUILDS_ID, vault.ownedRelicIds).find(
        r => r.relicId === browser.selectedRelicId,
      ) || null
    );
  }, [browser.selectedRelicId, catalog, vault.ownedRelicIds]);

  // The server owns the allowance; it tells us what is left and what it costs.
  const refreshAllowance = useMemo(
    () => ({ left: vault.refreshesLeft, nextCost: vault.nextRefreshCost ?? 0 }),
    [vault.refreshesLeft, vault.nextRefreshCost],
  );

  const onRefresh = useCallback(async () => {
    const result = await dispatch(refreshStoreSlots({ token }));
    if (!result?.success) {
      console.warn('[RelicVault] refresh failed', result?.error);
      return;
    }
    if (typeof result.newBalance === 'number' && onBalance) onBalance(result.newBalance);
  }, [dispatch, onBalance, token]);

  const onEquip = useCallback(
    guildId => purchase.equip(guildId),
    [purchase],
  );

  const handleClose = useCallback(() => {
    purchase.close();
    browser.closeDetail();
    setSelectedBadgeId(null);
    onClose && onClose();
  }, [browser, onClose, purchase]);

  const retry = useCallback(() => {
    dispatch(loadCatalog({ token }));
    if (token) dispatch(hydrateRelicVault({ token }));
  }, [dispatch, token]);

  const loading = vault.catalogStatus === 'loading' || vault.catalogStatus === 'idle';
  const failed = vault.catalogStatus === 'error';

  return (
    <Modal
      visible={!!visible}
      animationType="fade"
      transparent
      onRequestClose={handleClose}
      supportedOrientations={['portrait', 'landscape', 'landscape-left', 'landscape-right']}>
      <View
        style={[
          styles.root,
          pageBackground,
          {
            paddingTop: insets.top,
            paddingBottom: insets.bottom,
            paddingLeft: insets.left,
            paddingRight: insets.right,
          },
        ]}>
        {/* Fixed above the scroll view so the tabs, coins and close stay reachable. */}
        <CollectionTopBar
          balance={balance}
          coinGlow={browser.coinGlow}
          tab={tourOnly ? 'badges' : tab}
          onTabChange={changeTab}
          onClose={handleClose}
          {...(tourOnly ? { tabs: TOUR_COLLECTION_TABS } : {})}
        />

        {loading || failed ? (
          <CollectionStatus
            failed={failed}
            message={vault.catalogError}
            onRetry={retry}
          />
        ) : (
          <ScrollView
            ref={scrollRef}
            style={styles.fill}
            contentContainerStyle={styles.scrollContent}>
            <View
              style={[
                styles.content,
                isTablet && styles.contentTablet,
                isPhone && styles.contentPhone,
              ]}>
              <CollectionHero />

              {!tourOnly && tab === 'guardians' && (
                <GuardianPanel
                  catalog={catalog}
                  vault={vault}
                  balance={balance}
                  purchase={purchase}
                  onEquip={onEquip}
                />
              )}

              {!tourOnly && tab === 'relics' && (
                <>
                  <RelicStorePanel
                    catalog={catalog}
                    vault={vault}
                    balance={balance}
                    refreshAllowance={refreshAllowance}
                    purchase={purchase}
                    onRefresh={onRefresh}
                    onOpenVault={() =>
                      scrollRef.current?.scrollTo({ y: collectionY.current, animated: true })
                    }
                  />
                  <View
                    style={styles.collectionSection}
                    onLayout={e => {
                      collectionY.current = e.nativeEvent.layout.y;
                    }}>
                    <VaultBrowser catalog={catalog} vault={vault} browser={browser} />
                  </View>
                </>
              )}

              {(tourOnly || tab === 'badges') && (
                <BadgesView
                  catalog={tourOnly ? tourOnlyCatalog(catalog) : catalog}
                  ownedBadgeIds={vault.ownedBadgeIds}
                  onSelect={setSelectedBadgeId}
                />
              )}
            </View>
          </ScrollView>
        )}

        {tab !== 'badges' && (
          <PurchaseModal
            catalog={catalog}
            flow={purchase}
            balance={balance}
            ownedCompanionIds={vault.ownedCompanionIds}
            equippedCompanionId={vault.equippedCompanionId}
          />
        )}
        {tab === 'relics' && (
          <RelicDetailModal catalog={catalog} relic={selectedRelic} browser={browser} />
        )}
        {tab === 'badges' && (
          <BadgeDetailModal
            catalog={catalog}
            badgeId={selectedBadgeId}
            owned={vault.ownedBadgeIds.includes(selectedBadgeId)}
            onClose={() => setSelectedBadgeId(null)}
          />
        )}
      </View>
    </Modal>
  );
};

/** Shown while the catalog loads, and when it could not be reached. */
const CollectionStatus = ({ failed, message, onRetry }) => (
  <View style={styles.status}>
    {failed ? (
      <>
        <Text style={styles.statusTitle}>The collection is out of reach</Text>
        <Text style={styles.statusBody}>
          {message || 'Check your connection and try again.'}
        </Text>
        <TouchableOpacity
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel="Try again"
          style={styles.retry}>
          <Text style={styles.retryText}>Try again</Text>
        </TouchableOpacity>
      </>
    ) : (
      <>
        <ActivityIndicator size="large" color={COLORS.gold} />
        <Text style={styles.statusBody}>Opening the collection...</Text>
      </>
    )}
  </View>
);

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  fill: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    width: '100%',
    maxWidth: 1360,
    alignSelf: 'center',
    paddingTop: 24,
    paddingHorizontal: 32,
    paddingBottom: 80,
  },
  contentTablet: {
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 64,
  },
  contentPhone: {
    paddingTop: 16,
    paddingHorizontal: 14,
    paddingBottom: 56,
  },
  // The relic collection sits directly under the store slots on the Relics tab.
  collectionSection: {
    marginTop: 34,
  },
  status: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  statusTitle: {
    ...headingFont('800'),
    fontSize: TYPE.lg,
    color: COLORS.textWarm,
    textAlign: 'center',
  },
  statusBody: {
    ...bodyFont('500'),
    fontSize: TYPE.sm,
    color: COLORS.muted,
    marginTop: 10,
    textAlign: 'center',
  },
  retry: {
    marginTop: 20,
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  retryText: {
    ...headingFont('800'),
    fontSize: TYPE.sm,
    color: COLORS.text,
  },
});

export default RelicVaultOverlay;
